import { useEffect, useMemo, useState } from 'react';
import { UIButton } from '../primitives';
import { useTranslation } from 'react-i18next';

const RETINA_TITLE_MIN_WIDTH = 200;
const RETINA_TITLE_MIN_HEIGHT = 80;

function deriveTitleDisplaySize(width, height) {
  const safeWidth = Number(width) || 0;
  const safeHeight = Number(height) || 0;
  const isRetinaAsset =
    safeWidth >= RETINA_TITLE_MIN_WIDTH || safeHeight >= RETINA_TITLE_MIN_HEIGHT;

  if (!safeWidth || !safeHeight) {
    return null;
  }

  return {
    width: isRetinaAsset ? Math.max(1, Math.round(safeWidth / 2)) : safeWidth,
    height: isRetinaAsset ? Math.max(1, Math.round(safeHeight / 2)) : safeHeight,
  };
}

/**
 * HeroSlide — одиночный слайд hero-свайпера.
 * Figma: 318:3127 (slider, variant state=1, device=mobile, hasIcon=false).
 *
 * Layout:
 *   - full-bleed bg image (402×360dp)
 *   - bottom gradient overlay (#010103 0% → 0.24 → 1.0) — stops match Figma
 *   - absolute <img> с titleImageUrl в нижней зоне (figma §3 — заголовок-PNG)
 *   - CTA "Попробовать" — <UIButton type="tertiary" size="sm" rightIcon="forward">
 *
 * Strict-audit §6 closures (1 CRITICAL + 3 MINOR):
 *   1. ForwardIcon hand-crafted chevron 'M4.5 3l3 3-3 3' → <Icon name='forward'/>
 *      via UIButton rightIcon prop. Figma forward (123:2545) = полная стрелка
 *      (горизонтальная линия + шеврон), НЕ chevron-only.
 *   2. CTA local <button> with tailwind → <UIButton type='tertiary' size='sm'>
 *      primitive (matches Figma uiButton tertiary sm spec).
 *   3. Title position bottom-[72px] → bottom-[64px] (Figma: image 6 @ y=256,
 *      slide h=360, image-h=40 → bottom = 360-256-40 = 64px).
 *   4. CTA position right-6 (24px) → right-4 (16px) (Figma: button @ x=257,
 *      w=129, slide-w=402 → right = 402-257-129 = 16px).
 *
 * КРИТИЧНО (figma-first, acceptance §3 drift #1):
 *   Заголовок — НАСТОЯЩИЙ <img>, НЕ React-текст, НЕ CSS background-image.
 *   При titleImageUrl = null/undefined ничего title-специфичного не рендерим.
 *
 * Props:
 *   imageUrl           — URL фоновой картинки/gif (required)
 *   videoUrl?          — опциональный; если задан — <video autoplay muted loop>
 *   titleImageUrl?     — URL PNG-оверлея с title (absolute)
 *   titleImageWidth?   — точная ширина title-PNG в Figma-dp (default: 60% slide)
 *   titleImageHeight?  — точная высота title-PNG в Figma-dp (опционально)
 *   ctaText?           — label кнопки (default "Попробовать")
 *   onCtaClick?        — handler для tap CTA + самой карточки
 */
export default function HeroSlide({
  imageUrl,
  videoUrl,
  titleImageUrl,
  titleImageWidth,
  titleImageHeight,
  ctaText,
  onCtaClick,
}) {
  const { t } = useTranslation();
  const buttonText = ctaText || t('home.tryPreset');
  const [measuredTitleSize, setMeasuredTitleSize] = useState(null);
  const effectiveVideoUrl =
    videoUrl || (/\.(mp4|webm)(?:[?#].*)?$/i.test(imageUrl || '') ? imageUrl : null);

  useEffect(() => {
    setMeasuredTitleSize(null);
  }, [titleImageUrl, titleImageWidth, titleImageHeight]);

  const titleStyle = useMemo(() => {
    if (titleImageWidth || titleImageHeight) {
      return {
        width: titleImageWidth ? `${titleImageWidth}px` : undefined,
        height: titleImageHeight ? `${titleImageHeight}px` : undefined,
      };
    }

    if (measuredTitleSize?.width && measuredTitleSize?.height) {
      return {
        width: `${measuredTitleSize.width}px`,
        height: `${measuredTitleSize.height}px`,
      };
    }

    return { maxWidth: '60%' };
  }, [measuredTitleSize, titleImageHeight, titleImageWidth]);

  return (
    <div
      data-testid="hero-slide"
      className={[
        'relative w-full h-hero-h',
        'overflow-hidden',
        'rounded-home-16',
        'select-none',
      ].join(' ')}
      style={
        effectiveVideoUrl
          ? undefined
          : {
              backgroundImage: `url("${imageUrl}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundColor: '#010103',
            }
      }
      onClick={onCtaClick}
    >
      {effectiveVideoUrl && (
        <video
          src={effectiveVideoUrl}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          draggable={false}
        />
      )}

      <div
        aria-hidden="true"
        className="absolute inset-0 z-[1]"
        data-native-menu-shield="true"
      />

      {/* bottom gradient for legibility — Figma slide fills[1]
          stops: 0% a=0 → 49.5% a=0 → 75% a=0.24 → 100% a=1.0
          (bg-app RGB #010103). */}
      <div
        aria-hidden
        className="absolute inset-0 z-[2] pointer-events-none"
        style={{
          background:
            'linear-gradient(180deg, rgba(1,1,3,0) 0%, rgba(1,1,3,0) 49.5%, rgba(1,1,3,0.24) 75%, rgba(1,1,3,1) 100%)',
        }}
      />

      {/* title PNG overlay — Figma position: (24, 256) → left-6, bottom-[64px].
          Figma title rectangles: state=1 159×40, state=2 141×64, state=3 159×48.
          PNG exported at scale=2, поэтому явная ширина/высота из БД = Figma-dp. */}
      {titleImageUrl && (
        <img
          src={titleImageUrl}
          alt="title"
          className="absolute left-6 bottom-[64px] z-[3] h-auto pointer-events-none"
          style={titleStyle}
          onLoad={(e) => {
            if (titleImageWidth || titleImageHeight) return;
            const nextSize = deriveTitleDisplaySize(
              e.currentTarget.naturalWidth,
              e.currentTarget.naturalHeight,
            );
            if (nextSize) {
              setMeasuredTitleSize(nextSize);
            }
          }}
          draggable={false}
        />
      )}

      {/* CTA "Попробовать" — UIButton tertiary sm with forward arrow icon.
          Figma position: (257, 260) on 402×360 slide → right-4, bottom-[68px]. */}
      <div className="absolute right-4 bottom-[68px] z-[3]">
        <UIButton
          type="tertiary"
          size="sm"
          rightIcon="forward"
          onClick={(e) => {
            e.stopPropagation();
            onCtaClick && onCtaClick();
          }}
        >
          {buttonText}
        </UIButton>
      </div>
    </div>
  );
}
